// 1. websocket连接：判断浏览器是否兼容，获取 websocket url 并连接
// 2. 获取浏览器录音权限：判断浏览器是否兼容，获取浏览器录音权限
// 3. js获取浏览器录音数据
// 4. 将录音数据处理为文档要求的数据格式：采样率16k或8K、位长16bit、单声道；该操作属于纯数据处理，使用webWork处理
// 5. 根据要求（采用base64编码，每次发送音频间隔40ms，每次发送音频字节数1280B）将处理后的数据通过websocket传给服务器，
// 6. 实时接收 websocket 返回的数据并进行处理

import parser from 'fast-xml-parser'
import CryptoJS from 'crypto-js'
import { Base64 } from './js/base64js.js'

import { MediaManager } from './MediaManager'
import { message } from './message'
import { Logger } from './Logger'

export const Recorder_status = {
  NULL: 'null',
  INIT: 'init',
  INIT_FAIL: 'init fail',
  INIT_FINISH: 'init finish',
  RECORDING: 'recording',
  RECORD_END: 'record end',
  RECORD_FINISH: 'record finish',
}

const WebSocket = 'WebSocket' in window ? window.WebSocket : 'MozWebSocket' in window ? window.MozWebSocket : null

export const isSupportWebSocket = !!WebSocket

const AudioContext = window.AudioContext || window.webkitAudioContext

export const isSupportAudioContext = !!AudioContext

const OUTPUT_SAMPLE_RATE = 16000
// 在进入评测和上传流程前拦截过短或近似全静音的录音。
const MIN_VALID_AUDIO_DURATION = 500
const MIN_VALID_AUDIO_PEAK = 0.001

const customParamsKeys = ['ent', 'category', 'text', 'group', 'check_type']
const extractParams = (params = {}, defaultParams = {}) => {
  return customParamsKeys.reduce((p, key) => {
    const value = params[key] || defaultParams[key]
    if (value !== undefined) {
      p[key] = value
    }
    return p
  }, {})
}

export class IseRecorder {
  status = Recorder_status.NULL

  /**
   * @class IseRecorder
   *
   * - 评测参数
   * @see https://www.xfyun.cn/doc/Ise/IseAPI.html#%E4%B8%9A%E5%8A%A1%E5%8F%82%E6%95%B0%E8%AF%B4%E6%98%8E-business
   *
   * @param {object} [options={}]
   * @param {string} options.url IseWebSocket 链接
   * @param {string} options.appId 在平台申请的 APPID 信息，在控制台-我的应用-语音评测（流式版）页面获取
   * @param {object} options.logger 信息记录工具
   *
   * @param {object} [options.params={}] 评测参数，用作未传参时的默认值，也可以调用 startRecord 进行传参覆盖
   * @param {'cn_vip'|'en_vip'} [options.params.ent='cn_vip'] 评测参数， 中文：cn_vip 英文：en_vip
   * @param {'read_syllable'|'read_word'|'read_sentence'|'read_chapter'} [options.params.category='read_sentence'] 题型
   * @param {`\uFEFF${string}`} [options.params.text=''] 待评测文本 utf8 编码，需要加 utf8bom 头
   * @param {'adult'|'youth'|'pupil'} [options.params.group='adult'] 针对群体不同，相同试卷音频评分结果不同 （仅中文字、词、句、篇章题型支持），此参数会影响准确度得分
   * @param {'easy'|'common'|'hard'} [options.params.check_type='common'] 设置评测的打分及检错松严门限（仅中文引擎支持）
   */
  constructor(options) {
    this.setOptions(options)

    if (!this.appId || this.appId === 'APPID') {
      this.sendErrorLogToServer(message.NOT_APPID)
      throw Error(message.NOT_APPID)
    }

    // 记录音频数据
    this.audioData = []
    this.sendedAudioData = []
    this.closeEventCode = ''
    this.lastCloseEventCode = ''
    // 记录评测结果
    this.resultText = ''

    this.iseWebSocket = null
    this.audioContext = null
    this.mediaStream = null
    this.mediaSource = null
    this.scriptProcessor = null
    this.mediaManager = new MediaManager('audio')
    this.handlerInterval = null
    this.sendStartTimer = null
    this.audioStats = this.createAudioStats()
    this.isDestroyed = false
  }

  resolveOptions(options) {
    this.logger = options.logger || new Logger()
    this.url = options.url
    this.appId = options.appId
    this.appSecret = options.appSecret
    this.appKey = options.appKey
    this.$axios = options.$axios
  }

  setOptions(options) {
    this.options = options
    this.resolveOptions(options)
  }

  setStatus(status) {
    if (Object.values(Recorder_status).includes(status) && this.status !== status) {
      const lastStatus = this.status
      this.status = status
      this.options.onWillStatusChange?.(lastStatus, this.status)
    }
  }

  setResultText(text) {
    if (this.resultText !== text) {
      this.resultText = text
      this.options.onTextChange?.(this.resultText)
    }
  }

  createAudioStats() {
    return {
      sampleCount: 0,
      sumSquares: 0,
      peak: 0,
    }
  }

  resetRecordData() {
    this.clearHandlerInterval()
    this.clearSendStartTimer()
    this.audioData = []
    this.sendedAudioData = []
    this.resultText = ''
    this.audioStats = this.createAudioStats()
  }

  clearHandlerInterval() {
    if (this.handlerInterval) {
      clearInterval(this.handlerInterval)
      this.handlerInterval = null
    }
  }

  clearSendStartTimer() {
    if (this.sendStartTimer) {
      clearTimeout(this.sendStartTimer)
      this.sendStartTimer = null
    }
  }

  getAudioTrack() {
    return this.mediaStream?.getAudioTracks?.()[0]
  }

  isAudioTrackAvailable() {
    const track = this.getAudioTrack()
    return !!track && track.readyState === 'live' && track.enabled && !track.muted
  }

  updateAudioStats(samples) {
    let sumSquares = 0
    let peak = this.audioStats.peak
    for (let i = 0; i < samples.length; i++) {
      const value = samples[i]
      const absoluteValue = Math.abs(value)
      peak = Math.max(peak, absoluteValue)
      sumSquares += value * value
    }
    this.audioStats.sampleCount += samples.length
    this.audioStats.sumSquares += sumSquares
    this.audioStats.peak = peak
  }

  validateAudioData() {
    const { sampleCount, sumSquares, peak } = this.audioStats
    const sampleRate = this.audioContext?.sampleRate || 0
    const duration = sampleRate > 0 ? (sampleCount / sampleRate) * 1000 : 0
    const rms = sampleCount > 0 ? Math.sqrt(sumSquares / sampleCount) : 0
    const hasPcmData = this.audioData.length > 0 && this.audioData.some((byte) => byte !== 0)

    if (!this.isAudioTrackAvailable()) {
      return { valid: false, message: '麦克风连接已中断，请重新录音', duration, peak, rms }
    }
    if (duration < MIN_VALID_AUDIO_DURATION) {
      return { valid: false, message: '未采集到足够的录音数据，请重新录音', duration, peak, rms }
    }
    if (!hasPcmData || peak < MIN_VALID_AUDIO_PEAK) {
      return { valid: false, message: '未检测到有效声音，请检查麦克风后重新录音', duration, peak, rms }
    }
    return { valid: true, duration, peak, rms }
  }

  sendErrorLogToServer(logMessage) {
    if (!this.$axios) return
    const params = new FormData()
    params.append('message', logMessage)
    this.$axios.post('/tk/front/tongue/answer/error/logs', params).catch(() => {})
  }

  handleWebSocketMessage(message) {
    this.closeEventCode += 'p'
    // 识别结束
    const jsonData = JSON.parse(message)
    this.jsonD = message
    if (jsonData.message !== 'success') {
      this.closeEventCode += `${jsonData.message}`
    }
    if (jsonData.code === 0 && jsonData.data?.status === 2) {
      this.closeEventCode += 'r'
      this.iseWebSocket?.close()
    }
    if (jsonData.data?.data) {
      this.closeEventCode += 'q'
       // console.log('jsonData', jsonData)
      const data = Base64.decode(jsonData.data.data)
      // console.log('data===', data)
      const grade = parser.parse(data, {
        attributeNamePrefix: '',
        ignoreAttributes: false,
      })
      this.closeEventCode += 'z'
      this.setResultText(grade)
      this.options.getSID(jsonData?.sid)
    }
    if (jsonData.code !== 0) {
      this.closeEventCode += 's'
      this.iseWebSocket?.close()
      this.logger.log(jsonData.message, jsonData.code)
    }
  }

  connectWebSocket() {
    this.closeEventCode += 't'
    if (!this.url) {
      this.logger.warn(message.NOT_URL)
      return Promise.reject(new Error(message.NOT_URL))
    }
    this.closeEventCode += 'u'
    const iseWebSocket = (this.iseWebSocket = new WebSocket(this.url))
    console.log('create iseWebSocket')
    return new Promise((resolve, reject) => {
      let opened = false
      iseWebSocket.onopen = () => {
        // WebSocket 就绪不代表麦克风可用，必须确认音频链路仍在工作后才能进入录音态。
        if (this.isDestroyed || this.audioContext?.state !== 'running' || !this.isAudioTrackAvailable()) {
          const error = new Error('录音设备未就绪，请重新录音')
          this.abortRecording(error.message)
          reject(error)
          return
        }
        opened = true
        this.closeEventCode += 'c'
        console.log('iseWebSocket open')
        this.setStatus(Recorder_status.RECORDING)
        this.sendStartTimer = setTimeout(() => {
          this.sendStartTimer = null
          this.closeEventCode += 'v'
          this.webSocketSend()
        }, 40)
        resolve()
      }
      iseWebSocket.onmessage = (e) => {
        this.handleWebSocketMessage(e.data)
      }
      iseWebSocket.onerror = (e) => {
        const detail = e.message || JSON.stringify(e)
        this.closeEventCode += 'w' + this.jsonD
        this.logger.error(`iseWebSocket onerror`, this.closeEventCode, this.status, 'iseWebSocket.onerror:' + detail)
        this.sendErrorLogToServer('iseWebSocket onerror:' + detail)
        if (!opened) {
          reject(new Error('录音服务连接失败，请重试'))
        } else if (this.status === Recorder_status.RECORDING) {
          this.abortRecording('录音连接异常，请重新录音')
        }
      }
      iseWebSocket.onclose = (e) => {
        this.closeEventCode += 'x'
        this.closeEventCode += 'websocket 断开: ' + e.code + ' ' + e.reason + ' ' + e.wasClean
        if (!opened) {
          reject(new Error('录音服务连接已断开，请重试'))
        } else if (this.status === Recorder_status.RECORDING) {
          this.abortRecording('录音连接已断开，请重新录音')
        }
      }
    })
  }

  /**
   * 获取 WebSocket url
   */
  getIseWebSocketUrl() {
    const API_SECRET = this.appSecret
    const API_KEY = this.appKey
    if (!API_SECRET || !API_KEY) {
      return this.url
    }
    const baseUrl = 'wss://ise-api.xfyun.cn/v2/open-ise'
    const host = 'ise-api.xfyun.cn'
    const date = new Date().toGMTString()
    const algorithm = 'hmac-sha256'
    const headers = 'host date request-line'
    const signatureOrigin = `host: ${host}\ndate: ${date}\nGET /v2/open-ise HTTP/1.1`
    const signatureSha = CryptoJS.HmacSHA256(signatureOrigin, API_SECRET)
    const signature = CryptoJS.enc.Base64.stringify(signatureSha)
    const authorizationOrigin = `api_key="${API_KEY}", algorithm="${algorithm}", headers="${headers}", signature="${signature}"`
    const authorization = btoa(authorizationOrigin)
    const url = `${baseUrl}?authorization=${authorization}&date=${date}&host=${host}`
    return url
  }

  async recordInit() {
    this.closeEventCode += 'y'
    if (!isSupportAudioContext) {
      this.logger.warn(message.NOT_SUPPORT_WEB_AUTIO)
      throw new Error(message.NOT_SUPPORT_WEB_AUTIO)
    }
    if (!isSupportWebSocket) {
      this.logger.warn(message.NOT_SUPPORT_WEB_SOCKET)
      throw new Error(message.NOT_SUPPORT_WEB_SOCKET)
    }

    this.closeEventCode += 'g'
    this.setStatus(Recorder_status.INIT)

    // 每次录音都重建音频资源，避免复用 iOS 微信中已失效或静音的音频会话。
    this.releaseAudioResources()
    this.audioContext = new AudioContext()
    // 两个动作都在用户点击触发链中启动，满足 iOS 对 AudioContext 激活的限制。
    const resumePromise = this.audioContext.state === 'running' ? Promise.resolve() : this.audioContext.resume()
    const mediaPromise = this.mediaManager.getMediaPermissions()
    const [, stream] = await Promise.all([resumePromise, mediaPromise])

    if (this.isDestroyed) {
      stream.getTracks().forEach((track) => track.stop())
      throw new Error('录音组件已销毁')
    }

    this.closeEventCode += 'h'
    this.mediaStream = stream
    const audioTrack = this.getAudioTrack()
    if (!audioTrack || audioTrack.readyState !== 'live' || !audioTrack.enabled || audioTrack.muted) {
      throw new Error('麦克风设备不可用，请检查权限后重试')
    }
    if (this.audioContext.state !== 'running') {
      await this.audioContext.resume()
    }
    if (this.audioContext.state !== 'running') {
      throw new Error('录音环境未启动，请重新点击录音')
    }

    audioTrack.onmute = () => {
      if (this.status === Recorder_status.RECORDING) {
        this.abortRecording('麦克风已被系统中断，请重新录音')
      }
    }
    audioTrack.onended = () => {
      if (this.status === Recorder_status.RECORDING) {
        this.abortRecording('麦克风连接已断开，请重新录音')
      }
    }

    this.scriptProcessor = this.audioContext.createScriptProcessor(0, 1, 1)
    this.scriptProcessor.onaudioprocess = (e) => {
      if (this.status === Recorder_status.RECORDING) {
        const samples = e.inputBuffer.getChannelData(0)
        // 在降采样和 PCM 量化前统计原始波形，避免转换过程掩盖近似静音的数据。
        this.updateAudioStats(samples)
        this.audioData.push(...transAudioData(samples, this.audioContext.sampleRate))
      }
    }
    this.scriptProcessor.connect(this.audioContext.destination)

    this.mediaSource = this.audioContext.createMediaStreamSource(stream)
    this.mediaSource.connect(this.scriptProcessor)
    this.setStatus(Recorder_status.INIT_FINISH)
  }

  async startRecord(params) {
    if (this.isDestroyed) {
      throw new Error('录音组件已销毁')
    }
    if (this.status === Recorder_status.INIT || this.status === Recorder_status.RECORDING || this.status === Recorder_status.RECORD_END) {
      return false
    }
    this.params = extractParams(params, this.options.params)
    this.lastCloseEventCode = this.closeEventCode
    this.closeEventCode = ''
    this.resetRecordData()
    this.closeWebSocket()
    try {
      // 等待两项初始化都落定，避免其中一项失败后另一项晚到并遗留麦克风资源。
      const [urlResult, audioResult] = await Promise.allSettled([this.getWssUrl(), this.recordInit()])
      if (audioResult.status === 'rejected') {
        throw audioResult.reason
      }
      if (urlResult.status === 'rejected') {
        throw urlResult.reason
      }
      const url = urlResult.value
      if (!url) {
        throw new Error(message.NOT_URL)
      }
      this.url = url
      await this.connectWebSocket()
      return true
    } catch (error) {
      const errorMessage = error?.message || String(error)
      this.closeEventCode += 'i'
      this.setStatus(Recorder_status.INIT_FAIL)
      this.releaseAudioResources()
      this.closeWebSocket()
      this.logger.warn(errorMessage)
      this.sendErrorLogToServer('录音初始化失败:' + errorMessage)
      throw error
    }
  }

  stopRecord() {
    this.closeEventCode += 'f'
    if (this.status !== Recorder_status.RECORDING) {
      this.closeEventCode += 'e'
      // this.logger.log(this.status)
      return false
    }

    // 必须先校验再进入 RECORD_END，防止无效 PCM 触发 record-end 及后续上传。
    const validation = this.validateAudioData()
    if (!validation.valid) {
      const detail = `duration=${validation.duration.toFixed(0)},peak=${validation.peak},rms=${validation.rms}`
      this.abortRecording(validation.message, detail)
      return false
    }

    this.setStatus(Recorder_status.RECORD_END)
    // PCM 已保存在内存中，此时立即释放系统录音通道不会影响后续发送。
    this.releaseAudioResources()
    return true
  }

  abortRecording(errorMessage, detail = '') {
    if (this.status === Recorder_status.RECORD_FINISH && !this.audioContext && !this.mediaStream) return
    // 异常录音不再等待服务端结果，统一关闭链路并通知页面阻断上传。
    this.clearHandlerInterval()
    this.clearSendStartTimer()
    this.setStatus(Recorder_status.RECORD_FINISH)
    this.releaseAudioResources()
    this.closeWebSocket()
    this.options.onInvalidAudio?.(errorMessage, detail)
    this.sendErrorLogToServer(`${errorMessage}${detail ? ':' + detail : ''}`)
    this.audioData = []
    this.sendedAudioData = []
  }

  releaseAudioResources() {
    if (this.scriptProcessor) {
      this.scriptProcessor.onaudioprocess = null
      this.scriptProcessor.disconnect()
      this.scriptProcessor = null
    }
    if (this.mediaSource) {
      this.mediaSource.disconnect()
      this.mediaSource = null
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        track.onmute = null
        track.onended = null
        track.stop()
      })
      this.mediaStream = null
    }
    if (this.audioContext) {
      const audioContext = this.audioContext
      this.audioContext = null
      if (audioContext.state !== 'closed' && audioContext.close) {
        audioContext.close().catch(() => {})
      }
    }
  }

  closeWebSocket() {
    if (!this.iseWebSocket) return
    const socket = this.iseWebSocket
    this.iseWebSocket = null
    socket.onopen = null
    socket.onmessage = null
    socket.onerror = null
    socket.onclose = null
    if (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN) {
      socket.close()
    }
  }

  destroy() {
    this.isDestroyed = true
    this.clearHandlerInterval()
    this.clearSendStartTimer()
    this.closeWebSocket()
    this.releaseAudioResources()
    this.audioData = []
    this.sendedAudioData = []
    this.setStatus(Recorder_status.NULL)
  }

  cropAudioData(length = 1280) {
    const startIdx = this.sendedAudioData.length
    const audioData = this.audioData.slice(startIdx, startIdx + length)
    this.sendedAudioData.push(...audioData)
    return audioData
  }

  // 向 iseWebSocket 发送数据
  webSocketSend() {
    this.closeEventCode += 'k'
    const socket = this.iseWebSocket
    console.log('webSocketSend', socket?.readyState)
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return
    }
    this.closeEventCode += 'l'
    const audioData = this.cropAudioData()
    const params = {
      common: {
        app_id: this.appId,
      },
      business: {
        category: 'read_chapter', // read_syllable/单字朗读，汉语专有 read_word/词语朗读  read_sentence/句子朗读 https://www.xfyun.cn/doc/Ise/IseAPI.html#%E6%8E%A5%E5%8F%A3%E8%B0%83%E7%94%A8%E6%B5%81%E7%A8%8B
        rstcd: 'utf8',
        group: 'adult',
        sub: 'ise',
        tte: 'utf-8',
        cmd: 'ssb',
        auf: 'audio/L16;rate=16000',
        ent: 'cn_vip',
        aus: 1,
        aue: 'raw',
        // extra_ability: 'multi_dimension', // entirety联合使用
        rst: 'plain', // plain精简，entirety完整
        ise_unite: '1', // 1控制结果，0不控制结果
        ...this.params,
      },
      data: {
        status: 0,
        encoding: 'raw',
        data_type: 1,
        data: buffer2Base64(audioData),
      },
    }
    if (!params.business.text?.startsWith('\uFEFF')) {
      params.business.text = `\uFEFF${params.business.text || ''}`
    }

    socket.send(JSON.stringify(params))

    this.clearHandlerInterval()

    this.handlerInterval = setInterval(() => {
      // console.log('handlerIntervale')
      if (socket.readyState !== WebSocket.OPEN) {
        this.closeEventCode += 'm'
        // websocket 未连接
        this.audioData = []
        this.sendedAudioData = []
        this.clearHandlerInterval()
        this.setStatus(Recorder_status.RECORD_FINISH)
        return
      }
      // 最后一帧
      if (this.audioData.length === this.sendedAudioData.length) {
        this.closeEventCode += 'n'
        if (this.status === Recorder_status.RECORD_END) {
          this.closeEventCode += 'o'
          socket.send(
            JSON.stringify({
              business: {
                cmd: 'auw',
                aus: 4,
                aue: 'raw',
              },
              data: {
                status: 2,
                encoding: 'raw',
                data_type: 1,
                data: '',
              },
            })
          )
          this.audioData = []
          this.sendedAudioData = []
          this.clearHandlerInterval()
          this.setStatus(Recorder_status.RECORD_FINISH)
        }
        return false
      }
      const audioData = this.cropAudioData()
      // 中间帧
      socket.send(
        JSON.stringify({
          business: {
            cmd: 'auw',
            aus: 2,
            aue: 'raw',
          },
          data: {
            status: 1,
            encoding: 'raw',
            data_type: 1,
            data: buffer2Base64(audioData),
          },
        })
      )
    }, 10)
  }
}

function buffer2Base64(buffer) {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  const len = bytes.byteLength
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
}

function transAudioData(audioData, inputSampleRate) {
  return transcode(audioData)

  function transcode(audioData) {
    let output = to16kHz(audioData, inputSampleRate)
    output = to16BitPCM(output)
    output = Array.from(new Uint8Array(output.buffer))
    return output
  }

  function to16kHz(audioData, sampleRate) {
    var data = new Float32Array(audioData)
    if (sampleRate === OUTPUT_SAMPLE_RATE) return data
    // iOS 实际采样率可能随设备变化，按 AudioContext 的真实采样率转换到服务要求的 16 kHz。
    var fitCount = Math.round(data.length * (OUTPUT_SAMPLE_RATE / sampleRate))
    if (fitCount <= 1) return data.slice(0, fitCount)
    var newData = new Float32Array(fitCount)
    var springFactor = (data.length - 1) / (fitCount - 1)
    newData[0] = data[0]
    for (let i = 1; i < fitCount - 1; i++) {
      var tmp = i * springFactor
      var before = Math.floor(tmp).toFixed()
      var after = Math.ceil(tmp).toFixed()
      var atPoint = tmp - before
      newData[i] = data[before] + (data[after] - data[before]) * atPoint
    }
    newData[fitCount - 1] = data[data.length - 1]
    return newData
  }

  function to16BitPCM(input) {
    var dataLength = input.length * (16 / 8)
    var dataBuffer = new ArrayBuffer(dataLength)
    var dataView = new DataView(dataBuffer)
    var offset = 0
    for (var i = 0; i < input.length; i++, offset += 2) {
      var s = Math.max(-1, Math.min(1, input[i]))
      dataView.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true)
    }
    return dataView
  }
}
